import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFetchApartments } from '../../useFetchApartments';
import ImageGallery from './ImageGallery.tsx';
import {
  MapPin,
  Phone,
  Wifi,
  Tv,
  CookingPot,
  Refrigerator,
  Toilet,
  Bed,
  Waves,
  Home,
  Car,
  Utensils,
  BookOpen,
  ShowerHead,
  Dices,
} from 'lucide-react';
import { LanguageContext } from '../../App.jsx';
import Loading from '../Loading.jsx';
import Pricing from '../../Sections/Pricing.jsx';
import { Helmet } from 'react-helmet';

const Apartment = () => {
  const { language, t } = useContext(LanguageContext);
  const { name } = useParams();
  const { fetchApartmentData, loading } = useFetchApartments();
  const [apartmentData, setApartmentData] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const data = await fetchApartmentData(name);
      setApartmentData(data);
    }

    fetchData();
  }, []);

  const generateKeywords = () => {
    const keywords = [
      'apartman',
      'izdavanje',
      'smestaj',
      apartment.location.location,
      apartment.name[language],
      'bovansko jezero',
      ...apartment.mainFacts[language].map((feature) => feature.toLowerCase()),
    ];
    return keywords.join(', ');
  };

  if (loading || !apartmentData) {
    return <Loading />;
  }

  const { apartment, images } = apartmentData;


  const iconComponents = {
    MapPin: MapPin,
    Phone: Phone,
    WiFi: Wifi,
    Tv: Tv,
    CookingPot: CookingPot,
    Refrigerator: Refrigerator,
    Toilet: Toilet,
    Bed: Bed,
    Waves: Waves,
    Home: Home,
    Car: Car,
    Book: BookOpen,
    Dining: Utensils,
    ShowerHead: ShowerHead,
  };

  return (
    <div className="apartment-container">
      <Helmet>
        <title>
          {apartment.name[language]} - Apartment Rental near{' '}
          {apartment.location.location} | [Your Website Name]
        </title>
        <meta
          name="description"
          content={`Rent the comfortable ${apartment.name[language]} apartment near ${apartment.location.location}. Enjoy a relaxing stay with convenient amenities.`}
        />
        <meta name="keywords" content={generateKeywords()} />
      </Helmet>
      <div className="bg-white shadow-lg pt-[100px] lt950:pt-[50px]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {apartment.name[language]}
              </h1>
              <div className="flex items-center mt-2 text-gray-600">
                <MapPin className="w-5 h-5 mr-2" />
                <span>{apartment.location.location}</span>
              </div>
            </div>
            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center"
            >
              <Phone className="w-5 h-5 mr-2" />
              {t.button}
            </Link>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:flex w-full">
        <div className="w-auto">
          <ImageGallery images={images || []} />
        </div>
        <div className="w-full lg:w-1/2 flex items-center justify-center">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d23093.779315337146!2d21.67481303215027!3d43.65394332644292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475425003e7b0159%3A0x4491210acb1c5ad5!2sBella%20apartmani!5e0!3m2!1sen!2srs!4v1736171713622!5m2!1sen!2srs"
            className="iframe-map  h-[150px] sm:h-[200px] md:h-[300px] aspect-video self-center"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>


      {apartment.mainFacts && (
        <div className="container mx-auto px-4 py-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            {t.amenities}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {apartment.mainFacts[language].map((feature, index) => {
              const iconName = apartment.mainFacts["icons"][index];
              const IconComponent = iconComponents[iconName];
              return (
                <div
                  key={index}
                  className="bg-white p-4 rounded-lg shadow-xl p-6 flex flex-col items-center text-center"
                >
                  <div className="text-blue-600 mb-2">
                    {IconComponent ? <IconComponent /> : <Dices />}
                  </div>{' '}

                  <span className="text-gray-700 text-xl">{feature}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="container mx-auto px-4 py-12 bg-white mt-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-6">
          {t.aboutProperty}
        </h2>
        <div className="prose max-w-none text-gray-600 text-2xl">
          {apartment.description[language].map((text, index) => (
            <p key={index}>{text}</p>
          ))}
        </div>
      </div>

      {apartment.mainFacts && (
        <Pricing
          t={t}
          apartments={[apartmentData.apartment]}
          language={language}
        />
      )}
    </div>
  );
};

export default Apartment;
